import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { flexRender } from '@tanstack/react-table';
import { Copy, SortDown, SortUp } from '@styled-icons/boxicons-regular';
import { withErrorBoundary, InlineFilter } from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { getAttributeName, getCommonPinningStyles } from './TableHelpers';
import Tippy from '@tippyjs/react';
import { RadioButton } from '@superops/ui-design-system';

const SortUpIcon = styled(SortUp)`
  cursor: pointer;
  width: 20px;
  height: 20px;
`;

const SortDownIcon = styled(SortDown)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const FilterIcon = styled.div`
  cursor: pointer;
  margin-left: auto;
  margin-top: 2px;
`;

const DEFAULT_PIN_POSITION = 'left';

const TableHeader = (props) => {
  const {
    header,
    sortCurrentColumn,
    sortedCol,
    sortOrder,
    columnWidthPersonalization,
    tableName,
    user,
    saveColumnWidth,
    context,
    showFilters,
    toggleFilters,
    noHeaderBorder,
    headerBgColor
  } = props;

  const resizeByRef = React.useRef(false);
  const column = {
    ...header.column.columnDef,
    header: header.column.columnDef.Header || header.column.columnDef?.header
  };

  const isResizing = header.column.getIsResizing();
  const size = header.column.getSize();
  const field = header.column?.columnDef?.field;
  const fieldName = header.column?.columnDef?.field?.name;
  const sortAttribute = props?.getAttributeName || getAttributeName;

  const isPinned = header?.column?.getIsPinned();
  const enablePin = column?.enablePin;
  const disableUnpin = column?.disableUnpin;
  const defaultPin = column?.defaultPin;

  const pinColumn = (position) => {
    const pinPosition = position || DEFAULT_PIN_POSITION;
    header.column.pin(pinPosition);
  };

  const unPinColumn = () => {
    header?.column?.pin(false);
  };

  useEffect(() => {
    if (columnWidthPersonalization) {
      if (isResizing) {
        resizeByRef.current = true;
      }
      if (!isResizing && resizeByRef.current) {
        // SAVE TO PERSONALIZATION
        saveColumnWidth(tableName, user, fieldName, size);
      }
    }
  }, [isResizing]);

  // NEED TO REDACTOR THIS
  const attributeName = () => {
    switch (column?.field?.name) {
      case 'prioritizedId':
        return 'displayId';
      case 'client':
        return 'client.name';
      case 'site':
        return 'site.name';
      case 'requester':
        return 'requester.name';
      case 'technician':
        return 'technician.name';
      case 'techGroup':
        return 'techGroup.name';
      case 'firstResponseTimer':
        return 'firstResponseDueTime';
      case 'resolutionTimer':
        return 'resolutionDueTime';
      case 'sla':
        return 'sla.name';
      default:
        return column?.field?.name;
    }
  };
  // NEED TO REDACTOR THIS

  const getHeaderBgColor = () => {
    switch (headerBgColor) {
      case 'primary':
        return '#E3EDFF';
      case 'secondary':
        return '#F5F5FC';
      case 'warning':
        return '#FADCBE';
      case 'critical':
        return '#F7C1C1';
      case 'attention':
        return '#FDE9B3';
      case 'success':
        return '#C8E6C5';
      case 'default':
        return '#E0E0E0';
      default:
        return '#FFFFFF';
    }
  };

  const columnPinningStyles = getCommonPinningStyles(header?.column) || {};

  // Check if this column has any active filters applied to it
  const isFiltered = useMemo(() => {
    const operands = context?.viewConditions?.operands;
    if (!operands || !column?.field?.name) {
      return false;
    }

    const dataType = column?.field?.dataType;
    const currentField = column?.field?.name;

    // For entity data types, the attribute includes '.name' suffix
    const attributeToMatch =
      dataType === 'entity' ? `${currentField}.name` : currentField;

    // Check if any operand references this column's attribute
    return operands.some((operand) => operand.attribute === attributeToMatch);
  }, [
    context?.viewConditions?.operands,
    column?.field?.dataType,
    column?.field?.name
  ]);

  useEffect(() => {
    if (defaultPin === 'left' || defaultPin === 'right') {
      pinColumn(defaultPin);
    }
  }, []);

  return (
    <>
      <th
        key={header.id}
        colSpan={header.colSpan}
        style={{
          width: size,
          border: noHeaderBorder && '0px',
          background: getHeaderBgColor(),
          ...columnPinningStyles
        }}
        className="col-header"
      >
        <div className="header-wrapper">
          {header.isPlaceholder ? null : (
            <>
              <div className="header-inner">
                <div
                  className={`table-header-text ${
                    !column.disableSortBy ? 'cp' : null
                  }`}
                  onClick={() => sortCurrentColumn(header.column)}
                >
                  {flexRender(column?.header, header.getContext())}

                  {!column?.disableSortBy && (
                    <>
                      <div className="sort-wrapper">
                        {sortAttribute(field) === sortedCol ? (
                          <>
                            {sortOrder === 'ASC' && <SortUpIcon />}
                            {sortOrder === 'DESC' && <SortDownIcon />}
                          </>
                        ) : (
                          <div className="sort-icon">
                            <SortUpIcon />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {!column.disableFilters && (
                  <FilterIcon
                    className={`filter-icon ${isFiltered ? 'active' : ''}`}
                  >
                    <div onClick={toggleFilters}>
                      <SVGIcon name="Filter" />
                    </div>
                  </FilterIcon>
                )}

                {enablePin && !isPinned && (
                  <>
                    <Tippy
                      content={
                        <div className="pt10 pr30 pb10 pl10">
                          <div className="mb5">
                            <RadioButton
                              label="Left"
                              onChange={() => {
                                pinColumn('left');
                              }}
                            />
                          </div>

                          <div>
                            <RadioButton
                              label="Right"
                              onChange={() => {
                                pinColumn('right');
                              }}
                            />
                          </div>
                        </div>
                      }
                      duration={250}
                      delay={10}
                      placement="bottom-start"
                      appendTo={document.body}
                      allowHTML
                      theme="light"
                      interactive
                      className="table-pin-tippy-wrapper"
                      trigger="click"
                    >
                      <div className="pointer">
                        <SVGIcon name="PinnedWire" />
                      </div>
                    </Tippy>
                  </>
                )}

                {enablePin && isPinned && !disableUnpin && (
                  <>
                    <span onClick={unPinColumn} className="pointer">
                      <SVGIcon name="Pinned" />
                    </span>
                  </>
                )}

                {!column?.disableResizing && (
                  <>
                    <div
                      {...{
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
        {showFilters && (
          <>
            {!column.disableFilters && (
              <div className="filter-wrapper">
                <InlineFilter
                  allConditions={context.viewConditions}
                  meta={{
                    ...column.helpers,
                    field: column.field,
                    entity: column?.helpers?.entity || 'Ticket',
                    getAttributeName: props.getAttributeName || attributeName,
                    getEntityAttributeName: props.getEntityAttributeName
                  }}
                />
              </div>
            )}
          </>
        )}
      </th>
    </>
  );
};

export default React.memo(withErrorBoundary(TableHeader));
